import React, { useEffect, useMemo, useRef } from 'react';
import {
  Badge,
  Box,
  Grid,
  GridItem,
  HStack,
  SimpleGrid,
  Text,
  useToast,
  VStack,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FiBarChart2, FiActivity, FiCheckCircle, FiXCircle, FiStar, FiZap, FiCalendar, FiAlertCircle, FiArrowUpRight } from 'react-icons/fi';
import ReactApexChart from 'react-apexcharts';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import {
  UnifiedActionTile,
  UnifiedEmptyState,
  UnifiedLoadingState,
  UnifiedStatCard,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { useGetPartnerQuarterScore } from 'domains/user/hooks/useGetPartnerQuarterScore';
import { useGetUserRole } from 'domains/user/hooks/useGetUserRole';
import { isProfileComplete } from 'domains/user/utils/profileCompleteness';
import ProfileWarningBanner from 'domains/user/components/ProfileWarningBanner';
import useFetchTrucks from 'domains/truck/hooks/useFetchUserTrucks';
import { getPartnerDashboard } from 'domains/listing/services/listingService';
import { useTranslation } from 'i18n/TranslationProvider';
import { apexDefaultOptions, getGradientFill, getTierColors, areaDefaultOptions, getGlowOptions } from 'utilities/chartUtils';

export default function PartnerDashboardPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const warningToastShownRef = useRef(false);
  const { uid } = useFirebaseAuth();

  const [dashboardData, setDashboardData] = React.useState(null);
  const [dashboardLoading, setDashboardLoading] = React.useState(true);

  const { data: trucks, loading: trucksLoading } = useFetchTrucks();
  const { score: quarterScore } = useGetPartnerQuarterScore();
  const { role, user } = useGetUserRole();

  // Check if profile is complete for partner users
  const showProfileWarning = role === 'partner' && !isProfileComplete(user);

  useEffect(() => {
    if (!uid) return;
    const fetchDashboard = async () => {
      setDashboardLoading(true);
      try {
        const data = await getPartnerDashboard();
        setDashboardData(data);
      } catch (err) {
        console.error('[PartnerDashboardPage.fetchDashboard]', err);
      } finally {
        setDashboardLoading(false);
      }
    };
    fetchDashboard();
  }, [uid]);

  const stats = useMemo(() => {
    if (!dashboardData?.summary) {
      return {
        total: 0,
        active: 0,
        delivered: 0,
        completed: 0,
      };
    }
    return {
      total: dashboardData.summary.totalAssigned || 0,
      active: dashboardData.summary.activeQueue || 0,
      delivered: dashboardData.summary.deliveredListings || 0,
      completed: dashboardData.summary.completedListings || 0,
      // canceled is not directly in summary yet, but could be added
    };
  }, [dashboardData]);

  const activeQueue = useMemo(
    () => dashboardData?.activeQueue || [],
    [dashboardData],
  );

  const cmrAlerts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const warningThreshold = new Date(today);
    warningThreshold.setDate(warningThreshold.getDate() + 14);

    const allTrucks = Array.isArray(trucks) ? trucks : [];
    const expired = [];
    const expiringSoon = [];
    const missingDate = [];

    allTrucks.forEach((truck) => {
      if (!truck?.cmrInsuranceExpiryDate) {
        missingDate.push(truck);
        return;
      }
      const expiryDate = new Date(truck.cmrInsuranceExpiryDate);
      expiryDate.setHours(0, 0, 0, 0);

      if (expiryDate < today) {
        expired.push(truck);
      } else if (expiryDate <= warningThreshold) {
        expiringSoon.push(truck);
      }
    });

    return { expired, expiringSoon, missingDate };
  }, [trucks]);

  const deliveredCount = stats.delivered;
  const isLoadingStats = dashboardLoading || trucksLoading;
  const clampPercent = (value, fallback = 0) =>
    Math.max(0, Math.min(100, Number.isFinite(Number(value)) ? Number(value) : fallback));
  const breakdown = quarterScore?.breakdown || null;
  const scoreValue = clampPercent(quarterScore?.score ?? 100, 100);
  const partnerRatingValue = clampPercent(
    breakdown?.weightedPerformanceScore,
    scoreValue,
  );
  const speedScoreValue = clampPercent(
    breakdown?.speedScore,
    scoreValue,
  );
  const planningScoreValue = clampPercent(
    breakdown?.planningScore,
    scoreValue,
  );
  const currentRank = breakdown?.rank || 'Unranked';
  const delayDays = Number(quarterScore?.paymentDelayDays ?? 0);
  const eventsCount = Number(quarterScore?.eventsCount ?? 0);

  const requirementItems = useMemo(() => {
    const requirements = breakdown?.requirements || {};
    return [
      {
        key: 'ordersCount',
        label: t('Orders in quarter'),
        met: Boolean(requirements.ordersCount?.met),
        valueText: t('{{current}} / min {{target}}', {
          current: requirements.ordersCount?.current ?? 0,
          target: requirements.ordersCount?.minimum ?? 0,
        }),
      },
      {
        key: 'cancellationsPer100',
        label: t('Cancellation rate / 100 orders'),
        met: Boolean(requirements.cancellationsPer100?.met),
        valueText: t('{{current}} / max {{target}}', {
          current: requirements.cancellationsPer100?.current ?? 0,
          target: requirements.cancellationsPer100?.maximum ?? 0,
        }),
      },
      {
        key: 'damageClaimsPer100',
        label: t('Damage claims / 100 orders'),
        met: Boolean(requirements.damageClaimsPer100?.met),
        valueText: t('{{current}} / max {{target}}', {
          current: requirements.damageClaimsPer100?.current ?? 0,
          target: requirements.damageClaimsPer100?.maximum ?? 0,
        }),
      },
    ];
  }, [breakdown, t]);

  const completedByPeriod = useMemo(() => {
    if (!dashboardData?.timeline) {
      return { categories: [], data: [] };
    }
    return dashboardData.timeline;
  }, [dashboardData]);

  const tierVisuals = useMemo(() => getTierColors(partnerRatingValue), [partnerRatingValue]);

  const conversionChartOptions = useMemo(
    () => ({
      ...apexDefaultOptions,
      ...getGlowOptions('#3965FF'),
      chart: {
        ...apexDefaultOptions.chart,
        type: 'radialBar',
        sparkline: { enabled: true },
      },
      plotOptions: {
        radialBar: {
          hollow: { size: '60%' },
          track: { background: '#F1F5F9' },
          dataLabels: {
            name: { show: false },
            value: {
              show: true,
              fontSize: '18px',
              fontWeight: 700,
              color: '#3965FF',
              offsetY: 6,
              formatter: (value) => `${Math.round(value)}%`,
            },
          },
        },
      },
      colors: ['#3965FF'],
      labels: [t('Conversion')],
    }),
    [t],
  );

  const revenueChartOptions = useMemo(
    () => ({
      ...areaDefaultOptions,
      ...getGlowOptions('#3965FF'),
      colors: ['#3965FF'],
      xaxis: {
        ...areaDefaultOptions.xaxis,
        categories: dashboardData?.revenueTimeline?.categories || [],
      },
      yaxis: {
        ...areaDefaultOptions.yaxis,
        labels: {
          ...areaDefaultOptions.yaxis.labels,
          formatter: (value) => `€${value}`,
        },
      },
      tooltip: {
        ...areaDefaultOptions.tooltip,
        y: {
          formatter: (value) => `€${value.toLocaleString()}`,
        },
      },
    }),
    [dashboardData?.revenueTimeline],
  );

  const scoreChartOptions = useMemo(
    () => ({
      ...apexDefaultOptions,
      chart: {
        ...apexDefaultOptions.chart,
        type: 'radialBar',
        sparkline: { enabled: false },
        dropShadow: {
          enabled: true,
          top: 2,
          left: 0,
          blur: 4,
          opacity: 0.15,
          color: tierVisuals.main,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: {
            size: '72%',
            background: 'transparent',
          },
          track: {
            background: '#F1F5F9',
            strokeWidth: '97%',
            margin: 5,
          },
          dataLabels: {
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748B',
              offsetY: -10,
            },
            value: {
              show: true,
              fontSize: '30px',
              fontWeight: 700,
              color: '#1E293B',
              offsetY: 5,
              formatter: (value) => `${Math.round(value)}%`,
            },
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: [tierVisuals.gradient[0]],
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      labels: [t(currentRank)],
      colors: [tierVisuals.main],
      stroke: {
        lineCap: 'round',
      },
    }),
    [tierVisuals, t, currentRank],
  );

  const ordersChartOptions = useMemo(
    () => ({
      ...apexDefaultOptions,
      chart: {
        ...apexDefaultOptions.chart,
        type: 'bar',
      },
      colors: ['#3965FF'],
      fill: getGradientFill(['#7551FF']),
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: '50%',
          distributed: false,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => val || '',
        offsetY: -20,
        style: {
          fontSize: '10px',
          colors: ['#64748B'],
        },
      },
      xaxis: {
        ...apexDefaultOptions.xaxis,
        categories: completedByPeriod.categories,
      },
      yaxis: {
        ...apexDefaultOptions.yaxis,
        min: 0,
        forceNiceScale: true,
        labels: {
          ...apexDefaultOptions.yaxis.labels,
          formatter: (value) => `${Math.round(value)}`,
        },
      },
      tooltip: {
        ...apexDefaultOptions.tooltip,
        y: {
          formatter: (value) => t('{{value}} completed listings', { value }),
        },
      },
    }),
    [completedByPeriod.categories, t],
  );

  useEffect(() => {
    const warningCount = cmrAlerts.expired.length + cmrAlerts.expiringSoon.length;
    if (warningCount === 0 || warningToastShownRef.current) return;

    warningToastShownRef.current = true;
    toast({
      title: t('Truck CMR warning'),
      description:
        warningCount === 1
          ? t('One truck has CMR expiry risk. Review it in Fleet.')
          : t('{{count}} trucks have CMR expiry risk. Review them in Fleet.', { count: warningCount }),
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
  }, [cmrAlerts, toast, t]);

  const getStatusLabel = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'preplanned') return t('Preplanned');
    if (normalized === 'planned') return t('Planned');
    if (normalized === 'pickedup') return t('Picked up');
    if (normalized === 'delivered') return t('Delivered');
    if (normalized === 'canceled' || normalized === 'cancelled') return t('Canceled');
    return status || t('Unknown');
  };

  if (isLoadingStats) {
    return (
      <PageShell>
        <UnifiedLoadingState label={t('Loading dashboard...')} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          description={t(
            'Monitor current performance, fleet readiness, quote flow, and your active transport queue.',
          )}
          actions={
            <AiAssistantDrawer
              role="partner"
              screenContext={{
                routeKey: 'partner.home',
                role: 'partner',
                surface: 'partner-dashboard',
              }}
              buttonLabel={t('Open AI assistant')}
              subtitle={t('Ask for matching lanes, quote help, or the next operational step.')}
            />
          }
        />

        {showProfileWarning && <ProfileWarningBanner user={user} />}

        <SimpleGrid columns={{ base: 1, sm: 2, xl: 3, '2xl': 5 }} spacing={5}>
          <UnifiedStatCard 
            label={t('Total revenue')} 
            value={`€${Number(dashboardData?.summary?.totalRevenue || 0).toLocaleString()}`} 
            icon={FiZap} 
            accent="blue.500" 
            trend={dashboardData?.summary?.revenueTrend}
            trendLabel={t('vs last month')}
          />
          <UnifiedStatCard label={t('Active queue')} value={stats.active} icon={FiActivity} accent="orange.500" />
          <UnifiedStatCard label={t('Delivered')} value={deliveredCount} icon={FiCheckCircle} accent="green.500" />
          <UnifiedStatCard label={t('Quote win rate')} value={`${dashboardData?.summary?.quoteConversionRate || 0}%`} icon={FiStar} accent="purple.500" />
          <UnifiedStatCard label={t('Quarter rating')} value={`${Math.round(partnerRatingValue)}%`} icon={FiStar} accent="teal.500" />
        </SimpleGrid>

        {dashboardData?.summary?.revenueTrend > 0 && (
          <UnifiedSurfaceCard p={4} variant="glow" bg="emerald.50/30">
            <HStack spacing={3}>
              <Icon as={FiArrowUpRight} color="emerald.500" />
              <VStack align="start" spacing={0}>
                <Text fontWeight="700" size="sm">{t('Strategic Insight')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('Your revenue grew by {{percent}}% this month. You are currently among the top 10% of partners for growth!', { percent: dashboardData.summary.revenueTrend })}
                </Text>
              </VStack>
            </HStack>
          </UnifiedSurfaceCard>
        )}

        <Grid templateColumns={{ base: '1fr', xl: '1.2fr 1fr' }} gap={4}>
          <GridItem>
            <UnifiedSurfaceCard p={5}>
              <VStack align="stretch" spacing={4}>
                <Text fontWeight="700" fontSize="lg">{t('Revenue performance')}</Text>
                <Box h="300px">
                  <ReactApexChart
                    options={revenueChartOptions}
                    series={[{ name: t('Revenue'), data: dashboardData?.revenueTimeline?.data || [] }]}
                    type="area"
                    height="100%"
                  />
                </Box>
              </VStack>
            </UnifiedSurfaceCard>
          </GridItem>
          <GridItem>
            <UnifiedSurfaceCard p={5}>
              <VStack align="stretch" spacing={4}>
                <Text fontWeight="700" fontSize="lg">{t('Quote win rate')}</Text>
                <Box h="300px">
                  <ReactApexChart
                    options={conversionChartOptions}
                    series={[dashboardData?.summary?.quoteConversionRate || 0]}
                    type="radialBar"
                    height="100%"
                  />
                </Box>
              </VStack>
            </UnifiedSurfaceCard>
          </GridItem>
        </Grid>

        <UnifiedSurfaceCard p={5}>
          <VStack align="stretch" spacing={6}>
            <HStack justify="space-between" align="center" flexWrap="wrap">
              <VStack align="start" spacing={0}>
                <Text fontWeight="700" fontSize="lg">{t('Quarter Partner Score')}</Text>
                <Text fontSize="sm" color="gray.500">{t('Based on current quarter performance')}</Text>
              </VStack>
              <Badge colorScheme={delayDays > 0 ? 'yellow' : 'green'} fontSize="sm" px={3} py={1} borderRadius="full">
                {t('Payment delay: +{{days}} days', { days: delayDays })}
              </Badge>
            </HStack>
            
            <Flex direction={{ base: 'column', xl: 'row' }} align="center" gap={10}>
              <Box w={{ base: '100%', md: '300px' }} h="280px" position="relative">
                <ReactApexChart
                  options={scoreChartOptions}
                  series={[partnerRatingValue]}
                  type="radialBar"
                  height="100%"
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  textAlign="center"
                  pointerEvents="none"
                >
                  <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                    {t('Rating')}
                  </Text>
                </Box>
              </Box>
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4} flex="1">
                <UnifiedStatCard label={t('Rank')} value={t(currentRank)} icon={FiStar} accent="orange.400" />
                <UnifiedStatCard label={t('Quarter')} value={quarterScore?.quarterKey || t('Current')} icon={FiCalendar} accent="blue.400" />
                <UnifiedStatCard label={t('Speed Score')} value={`${Math.round(speedScoreValue)}%`} icon={FiZap} accent="yellow.400" />
                <UnifiedStatCard label={t('Planning Score')} value={`${Math.round(planningScoreValue)}%`} icon={FiActivity} accent="cyan.400" />
                <UnifiedStatCard label={t('Penalty events')} value={eventsCount} icon={FiAlertCircle} accent="red.400" />
                <UnifiedStatCard label={t('Quarter Score')} value={`${Math.round(scoreValue)}%`} icon={FiBarChart2} accent="green.400" />
              </SimpleGrid>
            </Flex>
          </VStack>
        </UnifiedSurfaceCard>

        <Box>
          <Text fontWeight="700" fontSize="lg" mb={4}>{t('Bronze requirements')}</Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {requirementItems.map((item) => (
              <UnifiedStatCard
                key={item.key}
                label={item.label}
                value={item.valueText}
                accent={item.met ? 'green.500' : 'red.500'}
                icon={item.met ? FiCheckCircle : FiXCircle}
                helpText={item.met ? t('Requirement met') : t('Requirement not met')}
              />
            ))}
          </SimpleGrid>
        </Box>

        <UnifiedSurfaceCard p={5}>
          <VStack align="stretch" spacing={4}>
            <Text fontWeight="700" fontSize="lg">{t('Completed listings per period')}</Text>
            <Box h="220px">
              {completedByPeriod.data.every((v) => v === 0) ? (
                <UnifiedEmptyState
                  title={t('No completion data yet')}
                  description={t('Statistics will appear here after your first delivered work.')}
                  minH="100%"
                  icon={FiBarChart2}
                />
              ) : (
                <ReactApexChart
                  options={ordersChartOptions}
                  series={[{ name: t('Completed listings'), data: completedByPeriod.data }]}
                  type="bar"
                  height="100%"
                />
              )}
            </Box>
          </VStack>
        </UnifiedSurfaceCard>

        {(cmrAlerts.expired.length > 0 ||
          cmrAlerts.expiringSoon.length > 0 ||
          cmrAlerts.missingDate.length > 0) && (
          <UnifiedSurfaceCard p={5}>
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="700" fontSize="lg">
                {t('Truck CMR status alerts')}
              </Text>
              {cmrAlerts.expired.length > 0 && (
                <Text color="red.400" fontWeight="600">
                  {t('{{count}} truck(s) have expired CMR insurance.', {
                    count: cmrAlerts.expired.length,
                  })}
                </Text>
              )}
              {cmrAlerts.expiringSoon.length > 0 && (
                <Text color="yellow.500" fontWeight="600">
                  {t('{{count}} truck(s) will expire within 14 days.', {
                    count: cmrAlerts.expiringSoon.length,
                  })}
                </Text>
              )}
              {cmrAlerts.missingDate.length > 0 && (
                <Text color="yellow.400" fontWeight="600">
                  {t('{{count}} truck(s) are missing CMR expiry date.', {
                    count: cmrAlerts.missingDate.length,
                  })}
                </Text>
              )}
              <Text fontSize="sm" color="gray.500">
                {t('Upload a new CMR Insurance in Fleet. The truck will stay pending until admin review.')}
              </Text>
              <UnifiedActionTile
                title={t('Open fleet to resolve CMR alerts')}
                description={t('Review truck documents and refresh anything close to expiry.')}
                to="/fleet"
              />
            </VStack>
          </UnifiedSurfaceCard>
        )}

        <Grid templateColumns={{ base: '1fr', xl: '1.2fr 1fr' }} gap={4}>
          <GridItem>
            <UnifiedSurfaceCard p={5}>
              <Text fontWeight="700" fontSize="lg" mb={4}>
                {t('Operational actions')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <UnifiedActionTile
                  title={t('Search deliveries')}
                  description={t('Review available marketplace listings and submit quotes.')}
                  to="/search"
                />
                <UnifiedActionTile
                  title={t('My quotes')}
                  description={t('Track accepted, pending, and rejected quotes in one place.')}
                  to="/my-quotes"
                />
                <UnifiedActionTile
                  title={t('Manage fleet')}
                  description={t('Update trucks, trailers, and availability.')}
                  to="/fleet"
                />
                <UnifiedActionTile
                  title={t('Partner invoices')}
                  description={t('Upload and track invoice payments.')}
                  to="/my-invoices"
                />
                <UnifiedActionTile
                  title={t('Profile settings')}
                  description={t('Keep company information and compliance details up to date.')}
                  to="/profile"
                />
              </SimpleGrid>
            </UnifiedSurfaceCard>
          </GridItem>

          <GridItem>
            <UnifiedSurfaceCard p={5}>
              <HStack justify="space-between" mb={4}>
                <Text fontWeight="700" fontSize="lg">
                  {t('Active queue')}
                </Text>
                <Badge colorScheme="brand">{activeQueue.length}</Badge>
              </HStack>
              {activeQueue.length === 0 ? (
                <UnifiedEmptyState
                  title={t('No active queue items')}
                  description={t('Accepted and in-progress work will appear here as your transport activity grows.')}
                  minH="220px"
                />
              ) : (
                <VStack align="stretch" spacing={3}>
                  {activeQueue.map((booking) => (
                    <UnifiedSurfaceCard
                      key={booking.id}
                      p={3}
                    >
                      <Text fontWeight="700" color="brand.500">
                        {booking.humanId || booking.id}
                      </Text>
                      <HStack justify="space-between" mt={1}>
                        <Text fontSize="sm" color="gray.500">
                          {t('ETA')}: {booking.arrivalDate ? new Date(booking.arrivalDate).toLocaleDateString() : t('N/A')}
                        </Text>
                        <Badge
                          colorScheme={
                            (booking.status || '').toLowerCase() === 'pickedup'
                              ? 'purple'
                              : 'brand'
                          }
                        >
                          {getStatusLabel(booking.status || 'prePlanned')}
                        </Badge>
                      </HStack>
                    </UnifiedSurfaceCard>
                  ))}
                </VStack>
              )}
            </UnifiedSurfaceCard>
          </GridItem>
        </Grid>
      </VStack>
    </PageShell>
  );
}
